import type { AdminCloudWatchEvent } from '@/types/admin-trace';

/**
 * SpanID-Korrelation (Spec 143 §5.3): Request- und Response-Events mit
 * gleicher spanId gehoeren zusammen und werden als Paar dargestellt.
 *
 * Referenz: archivierte groupBySpanId-Logik
 * (origin/archive/main-pre-reset-2026-05-18, CloudWatchEventsPanel).
 */
export interface SpanGroup {
  spanId: string;
  request: AdminCloudWatchEvent;
  response?: AdminCloudWatchEvent;
}

export type GroupedCwEvent = AdminCloudWatchEvent | SpanGroup;

export function isSpanGroup(item: GroupedCwEvent): item is SpanGroup {
  return 'request' in item && 'spanId' in item && !('eventId' in item);
}

/**
 * Gruppiert Events mit gleicher spanId zu Request/Response-Paaren.
 * Genau EIN Request + EIN Response pro Span werden gepaart — alle weiteren
 * Events (ohne spanId, ohne Partner oder Zusatz-Events eines Spans mit
 * 3+ Eintraegen) bleiben als Einzel-Events erhalten (kein Datenverlust).
 * Die chronologische Reihenfolge der API bleibt erhalten — das Paar steht
 * an der Position seines zuerst auftretenden Mitglieds.
 */
export function groupBySpanId(events: AdminCloudWatchEvent[]): GroupedCwEvent[] {
  const spanMap = new Map<string, AdminCloudWatchEvent[]>();
  for (const ev of events) {
    if (ev.spanId) {
      const group = spanMap.get(ev.spanId) ?? [];
      group.push(ev);
      spanMap.set(ev.spanId, group);
    }
  }

  // Pro Span genau ein Paar vorberechnen (Request + Response)
  const pairs = new Map<string, SpanGroup>();
  for (const [spanId, group] of spanMap) {
    if (group.length < 2) continue;
    const request = group.find((e) => e.type === 'cw_http_request') ?? group[0]!;
    const response = group.find(
      (e) => e !== request && (e.type === 'cw_http_response' || e.type === 'cw_http_error'),
    ) ?? group.find((e) => e !== request);
    pairs.set(spanId, { spanId, request, ...(response ? { response } : {}) });
  }

  const result: GroupedCwEvent[] = [];
  const usedIds = new Set<string>();

  for (const ev of events) {
    if (usedIds.has(ev.eventId)) continue;

    const pair = ev.spanId ? pairs.get(ev.spanId) : undefined;
    if (pair && (pair.request.eventId === ev.eventId || pair.response?.eventId === ev.eventId)) {
      usedIds.add(pair.request.eventId);
      if (pair.response) usedIds.add(pair.response.eventId);
      result.push(pair);
      continue;
    }

    // Einzel-Event: ohne spanId, ohne Partner — oder Zusatz-Event eines Spans
    usedIds.add(ev.eventId);
    result.push(ev);
  }

  return result;
}
